import React, {Fragment, useEffect, useRef, useState} from 'react';
import PropTypes from 'prop-types';
import 'moment/locale/it';
import 'moment/locale/en-gb';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import {Tab, Tabs} from 'react-bootstrap';
import CommonMetaTag from './common-meta-tag';
import {useTranslation} from 'react-i18next';
import {connect} from 'react-redux';
import {withRouter} from 'react-router-dom';
import {Button, Modal, Row, Col, Spinner, Table} from 'react-bootstrap';
import {showAffiliationModal, hideAffiliationModal, editAffiliation, deleteAffiliation, createAffiliation, selectAffiliation, activateDeactivateAffiliation, setPartnerAffiliationsActiveTab, deletePlayerFromAffiliation, editAffiliationPlayer, renewPlayerAffiliation, acceptOrRefuseAffiliationRenewRequest, getAffiliatedPlayers, getAffiliationRenewRequests} from '../actions/partner';
import {Formik} from 'formik';
import { Form } from 'react-bootstrap';
import { renewalType, affiliationType, durationType } from '../constants';
import { FaUser, FaPencilAlt, FaTrashAlt, FaCalendarAlt, FaUsers, FaUserCheck, FaIdBadge, FaSearch, FaPlus, FaCheck, FaUserPlus, FaSyncAlt, FaAddressCard, FaTrophy } from "react-icons/fa";
import { RiDeleteBinLine } from 'react-icons/ri';
import { MdAutorenew } from "react-icons/md";
import Dialog from 'react-bootstrap-dialog';
import ReactPaginate from 'react-paginate';
import checkIcon from '../images/membership_check_24.svg';
import DatePicker, {registerLocale} from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';
import it from 'date-fns/locale/it';
import {getRankingVerificationRequests, reviewRankingVerificationRequest, setAffiliatedPlayerRanking} from '../api';

registerLocale('it', it);

const RANKING_SPORTS = ['tennis', 'padel', 'pickleball'];
const AFFILIATION_PRICING_MODE = {
    discount: 'discount',
    dedicatedFieldCost: 'dedicated_field_cost'
};

// Keep two extra pixels beyond the required 8px to absorb subpixel transforms in Popper v1.
export const CERTIFICATE_CALENDAR_VIEWPORT_PADDING = 10;

export const keepCertificateCalendarInViewport = data => {
    if (!data || !data.offsets || !data.offsets.popper || typeof window === 'undefined') return data;

    const popper = data.offsets.popper;
    const viewportWidth = window.innerWidth || document.documentElement.clientWidth;
    const viewportHeight = window.innerHeight || document.documentElement.clientHeight;
    const width = popper.width || Math.max(0, popper.right - popper.left);
    const height = popper.height || Math.max(0, popper.bottom - popper.top);
    const maxLeft = Math.max(CERTIFICATE_CALENDAR_VIEWPORT_PADDING, viewportWidth - CERTIFICATE_CALENDAR_VIEWPORT_PADDING - width);
    const maxTop = Math.max(CERTIFICATE_CALENDAR_VIEWPORT_PADDING, viewportHeight - CERTIFICATE_CALENDAR_VIEWPORT_PADDING - height);
    const left = Math.min(Math.max(popper.left, CERTIFICATE_CALENDAR_VIEWPORT_PADDING), maxLeft);
    const top = Math.min(Math.max(popper.top, CERTIFICATE_CALENDAR_VIEWPORT_PADDING), maxTop);

    data.offsets.popper = {
        ...popper,
        left,
        top,
        right: left + width,
        bottom: top + height
    };
    return data;
};

export const CERTIFICATE_CALENDAR_POPPER_MODIFIERS = {
    flip: {
        enabled: true,
        behavior: ['bottom', 'top', 'right', 'left'],
        boundariesElement: 'viewport',
        padding: CERTIFICATE_CALENDAR_VIEWPORT_PADDING
    },
    preventOverflow: {
        enabled: true,
        boundariesElement: 'viewport',
        // Let Popper observe the overflow first so its flip modifier can choose the opposite side.
        escapeWithReference: true,
        padding: CERTIFICATE_CALENDAR_VIEWPORT_PADDING
    },
    certificateViewport: {
        enabled: true,
        order: 840,
        fn: keepCertificateCalendarInViewport
    }
};

export const shouldRefreshAffiliatedPlayers = tab => (
    tab === 'affiliation_requests' || tab === 'affiliated_users'
);

export const affiliationPlayerCount = affiliation => {
    const rawPlayerCount = affiliation && affiliation.playerCount;
    const playerCount = Number(rawPlayerCount);
    if (rawPlayerCount !== null && rawPlayerCount !== undefined && rawPlayerCount !== '' && Number.isFinite(playerCount) && playerCount >= 0) {
        return playerCount;
    }
    return Array.isArray(affiliation && affiliation.players) ? affiliation.players.length : 0;
};

const normalizeSport = sport => String(sport || '')
    .trim()
    .toLowerCase()
    .replace(/^(sport|replayer)[-_\s]*/, '');

const sportLabel = sport => {
    const normalized = normalizeSport(sport);
    return normalized ? normalized.charAt(0).toUpperCase() + normalized.slice(1) : '';
};

const buildSportPricing = (sports, current = []) => sports.map(sport => {
    const existing = current.find(row => normalizeSport(row.sport) === sport);
    return {
        sport,
        normalDiscount: existing ? Number(existing.normalDiscount || 0) : 0,
        participantRates: [1, 2, 3, 4].map(participantCount => {
            const rate = existing && (existing.participantRates || []).find(row => Number(row.participantCount) === participantCount);
            return {
                participantCount,
                hourlyPrice: rate ? Number(rate.hourlyPrice || 0) : 0
            };
        })
    };
});

const setRefValue = (ref, value) => {
    if (!ref) return;
    if (typeof ref === 'function') ref(value);
    else ref.current = value;
};

const CertificateCalendarTrigger = React.forwardRef(({value, onClick, onBlur, onFocus, onKeyDown, className, ariaLabel, stableRef}, ref) => {
    const setTriggerRef = node => {
        setRefValue(ref, node);
        setRefValue(stableRef, node);
    };

    return (
        <button
            type="button"
            ref={setTriggerRef}
            className={`${className || ''} partner-affiliations-certificate-trigger`.trim()}
            aria-label={ariaLabel}
            onClick={onClick}
            onBlur={onBlur}
            onFocus={onFocus}
            onKeyDown={onKeyDown}
        >
            <FaCalendarAlt aria-hidden="true"/>
            {value ? <span>{value}</span> : null}
        </button>
    );
});

export const AffiliationCertificateDatePicker = ({affiliation, editPlayer, triggerRef}) => {
    const {t} = useTranslation();
    const datePickerRef = useRef(null);
    const restoreFocusTimer = useRef(null);

    useEffect(() => () => {
        if (restoreFocusTimer.current !== null) clearTimeout(restoreFocusTimer.current);
    }, []);

    const restoreTriggerFocus = () => {
        if (restoreFocusTimer.current !== null) clearTimeout(restoreFocusTimer.current);
        restoreFocusTimer.current = setTimeout(() => {
            if (triggerRef && triggerRef.current) triggerRef.current.focus();
            else if (datePickerRef.current && datePickerRef.current.setFocus) datePickerRef.current.setFocus();
        }, 0);
    };

    const handleDateChange = val => {
        const update = editPlayer(
            affiliation.affiliationId,
            affiliation.playerId,
            affiliation.active,
            val,
            affiliation.payed
        );

        if (update && typeof update.then === 'function') {
            update.then(restoreTriggerFocus, restoreTriggerFocus);
        } else {
            restoreTriggerFocus();
        }
    };

    return (
        <DatePicker
            ref={datePickerRef}
            locale="it"
            className="form-control custom-datepicker"
            dateFormat="dd/MM/yy"
            portalId="partner-affiliations-certificate-calendar-portal"
            popperClassName="partner-affiliations-certificate-calendar-popper"
            popperPlacement="bottom-end"
            popperModifiers={CERTIFICATE_CALENDAR_POPPER_MODIFIERS}
            popperProps={{positionFixed: true}}
            preventOpenOnFocus
            selected={(affiliation.medicalCertificateTimestamp && new Date(affiliation.medicalCertificateTimestamp)) || null}
            onCalendarClose={restoreTriggerFocus}
            onChange={handleDateChange}
            customInput={<CertificateCalendarTrigger stableRef={triggerRef} ariaLabel={`${t('CALENDAR')}: ${t('CERTIFICATE')}`}/>}
        />
    );
};

export const AffiliationParticipantRates = ({handleChange, pricing, sportIndex, t}) => (
    <Form.Row className="partner-affiliations-participant-rates">
        {(pricing.participantRates || []).map((rate, rateIndex) => (
            <Col xs={6} className="partner-affiliations-participant-rate" key={rate.participantCount}>
                <Form.Group>
                    <Form.Label>{rate.participantCount === 1
                        ? t('DEDICATED_BASE_HOURLY_PRICE')
                        : t('DEDICATED_ADDITIONAL_PLAYER_HOURLY_PRICE', {count: rate.participantCount})}</Form.Label>
                    <Form.Control required type="number" min={0} step="0.01" name={`sportPricing.${sportIndex}.participantRates.${rateIndex}.hourlyPrice`} onChange={handleChange} value={rate.hourlyPrice}/>
                </Form.Group>
            </Col>
        ))}
    </Form.Row>
);

AffiliationParticipantRates.propTypes = {
    handleChange: PropTypes.func.isRequired,
    pricing: PropTypes.shape({
        participantRates: PropTypes.arrayOf(PropTypes.shape({
            hourlyPrice: PropTypes.oneOfType([PropTypes.number, PropTypes.string]).isRequired,
            participantCount: PropTypes.number.isRequired
        }))
    }).isRequired,
    sportIndex: PropTypes.number.isRequired,
    t: PropTypes.func.isRequired
};

export const PartnerAffiliations = ({
    profile,
    token,
    isAffiliationModalVisible,
    showAffiliationModal,
    hideAffiliationModal,
    newOrEditAffiliation,
    affiliations: affiliationsProp,
    affiliationsStatus,
    activateDeactivateAffiliation,
    deleteAffiliation,
    selectedAffiliation,
    showEditAffiliationModal,
    affiliatedPlayers: affiliatedPlayersProp,
    affiliatedPlayersLoading,
    partnerAffiliationsActiveTab,
    setActiveTab,
    deletePlayerFromAffiliation,
    editPlayer,
    renewPlayerAffiliation,
    affiliationRenewRequests: affiliationRenewRequestsProp,
    approveRenewRequest,
    refuseRenewRequest,
    refreshAffiliationRequests,
    refreshAffiliationRenewRequests,
    ownedFields: ownedFieldsProp,
    ownedFieldsStatus
}) => {
    const {t} = useTranslation();
    const affiliations = Array.isArray(affiliationsProp) ? affiliationsProp.filter(Boolean) : [];
    const affiliatedPlayers = Array.isArray(affiliatedPlayersProp) ? affiliatedPlayersProp.filter(Boolean) : [];
    const affiliationRenewRequests = Array.isArray(affiliationRenewRequestsProp) ? affiliationRenewRequestsProp.filter(Boolean) : [];
    const ownedFields = Array.isArray(ownedFieldsProp) ? ownedFieldsProp.filter(Boolean) : [];
    const partnerDataReady = Boolean(profile && token) && affiliationsStatus === 'ready' && ownedFieldsStatus === 'ready';
    const partnerDataFailed = affiliationsStatus === 'error' || ownedFieldsStatus === 'error';
    const availableSports = [...new Set(ownedFields
        .filter(field => field.bookable !== false)
        .map(field => normalizeSport(field.sport))
        .filter(Boolean))];

    const [requestsOffset, setRequestsOffset] = useState(0);
    const [requestsFilter, setRequestsFilter] = useState("");
    const [renewRequestsOffset] = useState(0);
    const [renewRequestsFilter, setRenewRequestsFilter] = useState("");
    const [affiliatedPlayersOffset, setAffiliatedPlayersOffset] = useState(0);
    const [affiliatedNameFilter, setAffiliatedNameFilter] = useState("");
    const [affiliatedAffiliationFilter, setAffiliatedAffiliationFilter] = useState("");
    const [affiliatedStateFilter, setAffiliatedStateFilter] = useState("");
    const [rankingRequests, setRankingRequests] = useState([]);
    const [rankingRequestsLoading, setRankingRequestsLoading] = useState(false);
    const [rankingRequestsMessage, setRankingRequestsMessage] = useState(null);
    const [rankingRequestEdits, setRankingRequestEdits] = useState({});
    const [renewingAffiliationKey, setRenewingAffiliationKey] = useState(null);
    const [rankingAssessment, setRankingAssessment] = useState(null);
    const [rankingAssessmentLoading, setRankingAssessmentLoading] = useState(false);
    const [rankingAssessmentMessage, setRankingAssessmentMessage] = useState(null);
    const certificateTriggerRefs = useRef(new Map());

    const getCertificateTriggerRef = affiliation => {
        const key = `${affiliation.affiliationId}-${affiliation.playerId}`;
        if (!certificateTriggerRefs.current.has(key)) {
            certificateTriggerRefs.current.set(key, React.createRef());
        }
        return certificateTriggerRefs.current.get(key);
    };

    useEffect(() => {
        if (partnerDataReady && shouldRefreshAffiliatedPlayers(partnerAffiliationsActiveTab) && !affiliatedPlayersLoading) {
            refreshAffiliationRequests();
        }
        // Requests and affiliated users share the same endpoint and Redux collection.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partnerDataReady]);

    useEffect(() => {
        if (partnerDataReady && partnerAffiliationsActiveTab === 'ranking_verifications') {
            refreshRankingRequests();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [partnerAffiliationsActiveTab, partnerDataReady]);

    const refreshRankingRequests = async () => {
        setRankingRequestsLoading(true);
        setRankingRequestsMessage(null);
        const res = await getRankingVerificationRequests(token, 'pending');
        if (res.status === 200) {
            setRankingRequests(res.json.requests || []);
            const nextEdits = {};
            (res.json.requests || []).forEach(request => {
                nextEdits[request.id] = {
                    classification: request.partnerClassification || request.declaredClassification || '',
                    licenseNumber: request.declaredLicenseNumber || '',
                    bestClassification: request.declaredBestClassification || '',
                    note: request.partnerNote || ''
                };
            });
            setRankingRequestEdits(nextEdits);
        } else {
            setRankingRequestsMessage(`Non e' stato possibile caricare le richieste ranking: ${res.json && res.json.error ? res.json.error : res.status}`);
        }
        setRankingRequestsLoading(false);
    };

    const updateRankingRequestEdit = (requestId, patch) => {
        setRankingRequestEdits(current => ({
            ...current,
            [requestId]: {
                ...(current[requestId] || {}),
                ...patch
            }
        }));
    };

    const reviewRankingRequest = async (request, action) => {
        const edits = rankingRequestEdits[request.id] || {};
        setRankingRequestsLoading(true);
        setRankingRequestsMessage(null);
        const res = await reviewRankingVerificationRequest(request.id, {
            action,
            classification: edits.classification,
            licenseNumber: edits.licenseNumber,
            bestClassification: edits.bestClassification,
            note: edits.note
        }, token);
        if (res.status === 200) {
            setRankingRequestsMessage(action === 'confirm' ? t('RANKING_CONFIRMED') : t('REQUEST_REJECTED'));
            await refreshRankingRequests();
        } else {
            setRankingRequestsMessage(t('OPERATION_FAILED_WITH_REASON', {reason: res.json && res.json.error ? res.json.error : res.status}));
        }
        setRankingRequestsLoading(false);
    };

    if (!partnerDataReady) {
        return (
            <Fragment>
                <CommonMetaTag/>
                <main className="partner-affiliations-page">
                    <section className="partner-affiliations-panel">
                        <div
                            className="partner-affiliations-loading partner-affiliations-users-loading"
                            role={partnerDataFailed ? 'alert' : 'status'}
                            aria-live="polite"
                        >
                            {!partnerDataFailed ? <span className="partner-affiliations-loading-icon"><Spinner animation="border"/></span> : <FaAddressCard/>}
                            <h5>{t(partnerDataFailed ? 'AFFILIATION_FAILED' : 'LOADING_DATA_IN_PROGRESS')}</h5>
                        </div>
                    </section>
                </main>
            </Fragment>
        );
    }

    const affiliatedAffiationOptions = affiliations.filter(a => affiliationPlayerCount(a) > 0).map(a => ({value: a.affiliationId, label: a.name}));

    const affiliationPlayers = affiliations.reduce((players, affiliation) => players.concat(affiliation.players || []), []);
    const affiliatedPlayersForSummary = affiliationPlayers.length > 0
        ? affiliationPlayers
        : affiliatedPlayers.filter(affiliation => affiliation.active !== null);
    const affiliatedUniquePlayersCount = new Set(
        affiliatedPlayersForSummary
            .map(player => {
                if (typeof player !== 'object') return String(player);
                return player.playerId || player.id || player.uid ||
                    (player.player && (player.player.playerId || player.player.id || player.player.uid)) ||
                    (player.user && (player.user.id || player.user.uid || player.user.email));
            })
            .filter(playerId => playerId !== undefined && playerId !== null)
            .map(String)
    ).size;

    const completeAffiliatedPlayers = affiliatedPlayers.filter(affiliation => affiliation.player && affiliation.player.user && affiliation.affiliation);
    const completeRenewRequests = affiliationRenewRequests.filter(affiliation => affiliation.player && affiliation.player.user && affiliation.affiliation);

    const playerRequests = completeAffiliatedPlayers.filter(affiliation => affiliation.active === null && (requestsFilter === "" || String(affiliation.player.name || '').toLowerCase().indexOf(requestsFilter.toLowerCase()) > -1 || String(affiliation.player.surname || '').toLowerCase().indexOf(requestsFilter.toLowerCase()) > -1));
    
    const renewRequests = completeRenewRequests.filter(affiliation => renewRequestsFilter === "" || String(affiliation.player.name || '').toLowerCase().indexOf(renewRequestsFilter.toLowerCase()) > -1 || String(affiliation.player.surname || '').toLowerCase().indexOf(renewRequestsFilter.toLowerCase()) > -1);

    const affiliated = completeAffiliatedPlayers.filter(affiliation => affiliation.active !== null &&
        (affiliatedNameFilter === "" || String(affiliation.player.name || '').toLowerCase().indexOf(affiliatedNameFilter.toLowerCase()) > -1 || String(affiliation.player.surname || '').toLowerCase().indexOf(affiliatedNameFilter.toLowerCase()) > -1) &&
        (affiliatedAffiliationFilter === "" || String(affiliation.affiliationId) === String(affiliatedAffiliationFilter)) &&
        (affiliatedStateFilter === "" || (affiliatedStateFilter === 'active' && affiliation.active) || (affiliatedStateFilter === 'nonactive' && !affiliation.active) || (affiliatedStateFilter === 'payed' && affiliation.payed) || (affiliatedStateFilter === 'toBePayed' && !affiliation.payed) || (affiliatedStateFilter === 'expired' && affiliation.expiryTimestamp !== null && new Date(affiliation.expiryTimestamp) < new Date()) || (affiliatedStateFilter === 'expiredCertificate' && affiliation.medicalCertificateTimestamp !== null && new Date(affiliation.medicalCertificateTimestamp) < new Date()) || (affiliatedStateFilter === 'noCertificate' && affiliation.medicalCertificateTimestamp === null))
    );

    const affiliationPaymentCheck = affiliationId => {
        const affiliation = affiliations.find(a => a.affiliationId === affiliationId);
        // During a direct-route bootstrap affiliated players may arrive before their affiliation metadata.
        return !affiliation || affiliation.onlinePayment == null;
    }

    let dialog = null;

    const affiliationPlayerKey = affiliation => `${affiliation.affiliationId}-${affiliation.playerId}`;
    const affiliationPlayerName = affiliation => `${affiliation.player.name} ${affiliation.player.surname}`.trim();
    const openRankingAssessment = (affiliation, sport) => {
        const currentRanking = affiliation.player.rankings && affiliation.player.rankings[sport];
        setRankingAssessment({
            playerId: affiliation.playerId,
            playerName: affiliationPlayerName(affiliation),
            sport,
            level: currentRanking && currentRanking.level !== null ? Number(currentRanking.level).toFixed(1) : '',
            note: ''
        });
        setRankingAssessmentMessage(null);
    };
    const saveRankingAssessment = async () => {
        if (!rankingAssessment) return;
        setRankingAssessmentLoading(true);
        setRankingAssessmentMessage(null);
        const res = await setAffiliatedPlayerRanking(
            rankingAssessment.playerId,
            rankingAssessment.sport,
            {level: rankingAssessment.level, note: rankingAssessment.note},
            token
        );
        if (res.status === 200) {
            setRankingAssessment(null);
            refreshAffiliationRequests();
        } else {
            setRankingAssessmentMessage(t('PARTNER_RANKING_SAVE_ERROR'));
        }
        setRankingAssessmentLoading(false);
    };
    const renderRankingCell = (affiliation, sport) => {
        const ranking = affiliation.player.rankings && affiliation.player.rankings[sport];
        return (
            <td className="partner-affiliations-ranking-cell" data-label={t(`SPORT_${sport.toUpperCase()}`)}>
                <Button
                    type="button"
                    className="partner-affiliations-ranking-button"
                    onClick={() => openRankingAssessment(affiliation, sport)}
                    title={t('PARTNER_SET_REPLAYER_LEVEL')}
                >
                    <span>{ranking ? Number(ranking.level).toFixed(1) : '--'}</span>
                    <small>{ranking ? t('RELIABILITY_PERCENT', {value: ranking.reliability || 0}) : t('NOT_CONFIGURED')}</small>
                    <FaPencilAlt aria-hidden="true"/>
                </Button>
            </td>
        );
    };
    const isRenewalWindowOpen = affiliation => {
        if (!affiliation.renewable || affiliation.renewAlreadyRequested) return false;
        if (!affiliation.expiryTimestamp) return true;
        const expiryDate = new Date(affiliation.expiryTimestamp);
        if (Number.isNaN(expiryDate.getTime())) return true;
        const renewalStartDate = new Date(expiryDate);
        renewalStartDate.setDate(renewalStartDate.getDate() - 15);
        return new Date() >= renewalStartDate;
    };

    const executeRenewPlayerAffiliation = async affiliation => {
        const key = affiliationPlayerKey(affiliation);
        if (renewingAffiliationKey !== null || !isRenewalWindowOpen(affiliation)) return;
        setRenewingAffiliationKey(key);
        try {
            await renewPlayerAffiliation(affiliation.playerId, affiliation.affiliationId);
        } finally {
            setRenewingAffiliationKey(null);
        }
    };

    const confirmRenewPlayerAffiliation = affiliation => {
        if (!isRenewalWindowOpen(affiliation) || renewingAffiliationKey !== null) return;
        dialog.show({
          title: t('RENEW_AFFILIATION'),
          body: (
            <Fragment>
                <Form.Label>
                    {t('RENEW_PLAYER_AFFILIATION_CONFIRM_BODY', {
                        player: affiliationPlayerName(affiliation),
                        affiliation: affiliation.affiliation.name,
                        expiryDate: affiliation.expiryTimestamp ? new Date(affiliation.expiryTimestamp).toLocaleDateString() : t('NOTHING')
                    })}
                </Form.Label>
                <Form.Label>
                    <strong>{t('RENEW_PLAYER_AFFILIATION_CONFIRM_WARNING')}</strong>
                </Form.Label>
            </Fragment>
          ),
          actions: [
            Dialog.Action(
              t('CONFIRM'),
              () => executeRenewPlayerAffiliation(affiliation),
              'btn partner-affiliations-dialog-primary'
            ),
            Dialog.Action(
              t('CANCEL'),
              null,
              'btn partner-affiliations-dialog-secondary'
            )
          ],
          bsSize: 'small',
          onHide: dialog => {
            dialog.hide();
          }
        });
    };

    const activateDeactivateAffiliationDialog = (value, affiliation) => {
        dialog.show({
          title: value ? t('ACTIVATE_AFFILIATION') : t('DEACTIVATE_AFFILIATION'),
          body: value ? 
          <Fragment>
            <Form.Label>
                {t('ACTIVATE_AFFILIATION_DESC')}
            </Form.Label>
            <Form.Label>
                {t('ACTIVATE_AFFILIATION_DESC_TWO')}
            </Form.Label>
          </Fragment>
          : t('DEACTIVATE_AFFILIATION_DESC'),
          actions: [
            Dialog.Action(
              value ? t('YES_ACTIVATE') : t('YES_DEACTIVATE'),
              () => activateDeactivateAffiliation(value, affiliation),
              'btn partner-affiliations-dialog-primary'
            ),
            Dialog.Action(
              t('CANCEL'),
              null,
              'btn partner-affiliations-dialog-secondary'
            )
          ],
          bsSize: 'small',
          onHide: dialog => {
            dialog.hide();
          }
        });
      };

      const deleteAffiliationDialog = (affiliation) => {
        dialog.show({
          title: t('DELETE_AFFILIATION'),
          body: t('DELETE_AFFILIATION_DESC', {name: affiliation.name}),
          actions: [
            Dialog.Action(
              t('DELETE'),
              () => deleteAffiliation(affiliation.affiliationId),
              'btn partner-affiliations-dialog-danger'
            ),
            Dialog.Action(
              t('CANCEL'),
              null,
              'btn partner-affiliations-dialog-secondary'
            )
          ],
          bsSize: 'small',
          onHide: dialog => {
            dialog.hide();
          }
        });
      };

      const confirmDeletePlayerFromAffiliation = (playerId,affiliationId) => {
        dialog.show({
          title: t('CONFIRM'),
          body: t('CONFIRM_DELETE_PLAYER_FROM_GROUP'),
          actions: [
            Dialog.Action(
              t('DELETE'),
              () => deletePlayerFromAffiliation(playerId,affiliationId),
              'btn partner-affiliations-dialog-danger'
            ),
            Dialog.Action(
              t('CANCEL'),
              null,
              'btn partner-affiliations-dialog-secondary'
            )
          ],
          bsSize: 'small',
          onHide: dialog => {
            dialog.hide();
          }
        });
      };

      const confirmChangeStateDialog = (checked, a) => {
        dialog.show({
            title: t('CONFIRM'),
            body: t('CONFIRM_ACTIVATE_SUBSCRIPTION'),
            actions: [
              Dialog.Action(
                t('YES_ACTIVATE'),
                () => editPlayer(a.affiliationId, a.playerId, checked, a.medicalCertificateTimestamp, a.payed),
                'btn partner-affiliations-dialog-primary'
              ),
              Dialog.Action(
                t('CANCEL'),
                null,
                'btn partner-affiliations-dialog-secondary'
              )
            ],
            bsSize: 'small',
            onHide: dialog => {
              dialog.hide();
            }
          });
      }


    const getMinimumDuration = durationSelect => {
        if (durationSelect === durationType.days) return 7;
        return 1;
    }

    const getMaximumDuration = durationSelect => {
        if (durationSelect === durationType.days) return 365;
        if (durationSelect === durationType.months) return 12;
        return 1;
    }

    const requests = currentItems => {
        if (affiliatedPlayersLoading) {
            return (
                <div className="partner-affiliations-loading partner-affiliations-users-loading" role="status" aria-live="polite">
                    <span className="partner-affiliations-loading-icon"><Spinner animation="border"/></span>
                    <div>
                        <h5>{t('AFFILIATED_USERS_SEARCH_IN_PROGRESS')}</h5>
                        <p>{t('AFFILIATED_USERS_SEARCH_IN_PROGRESS_DESC')}</p>
                    </div>
                </div>
            );
        }

        return (
            currentItems.length > 0 ?
            <Row className="justify-content-center partner-affiliations-table-row">
                <Col xs={12}>
                    <Table hover responsive className="partner-affiliations-table">
                        <thead>
                            <tr>
                                <th>{t('MEMBERSHIP')}</th>
                                <th>{t('USER')}</th>
                                <th>{t('CONTACT_DETAILS')}</th>
                                <th>{t('REQUEST')}</th>
                                <th className="text-center">{t('APPROVE')}</th>
                                <th className="text-center">{t('DELETE')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map(a => (
                                <tr key={`${a.affiliationId}-${a.playerId}`}>
                                    <td className="col-md-2">{a.affiliation.name}</td>
                                    <td className="col-md-2">{a.player.name + ' ' + a.player.surname}</td>
                                    <td className="col-md-2">{a.player.user.email + ' ' + a.player.phoneNumber}</td>
                                    <td className="col-md-2">{new Date(a.requestTimestamp).toLocaleDateString()}</td>
                                    <td className="col-md-2 text-center">
                                        <Button className="partner-affiliations-approve-button" variant="outline-primary" onClick={() => editPlayer(a.affiliationId, a.playerId, true, null, false)}>
                                            <img src={checkIcon} alt=""/>
                                            <span>{t('APPROVE')}</span>
                                        </Button>
                                    </td>
                                    <td className="col-md-2 text-center">
                                        <Button className="partner-affiliations-icon-button is-danger" variant="outline-secondary" onClick={() => confirmDeletePlayerFromAffiliation(a.playerId, a.affiliationId)}>
                                            <RiDeleteBinLine size="1.5em"/>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>
            : <div className="partner-affiliations-empty"><FaUsers/><h6>{requestsFilter === "" ? t('NO_REQUESTS') : t('SEARCH_REQUEST_FAILED')}</h6></div>
        );
    };

    const renewRequestsTable = currentItems => {
        return (
            currentItems.length > 0 ?
            <Row className="justify-content-center partner-affiliations-table-row">
                <Col xs={12}>
                    <Table hover responsive className="partner-affiliations-table">
                        <thead>
                            <tr>
                                <th>{t('MEMBERSHIP')}</th>
                                <th>{t('USER')}</th>
                                <th>{t('CONTACT_DETAILS')}</th>
                                <th>{t('REQUEST')}</th>
                                <th className="text-center">{t('APPROVE')}</th>
                                <th className="text-center">{t('DELETE')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map(a => (
                                <tr key={a.requestId}>
                                    <td className="col-md-2">{a.affiliation.name}</td>
                                    <td className="col-md-2">{a.player.name + ' ' + a.player.surname}</td>
                                    <td className="col-md-2">{a.player.user.email + ' ' + a.player.phoneNumber}</td>
                                    <td className="col-md-2">{new Date(a.requestTimestamp).toLocaleDateString()}</td>
                                    <td className="col-md-2 text-center">
                                        <Button className="partner-affiliations-approve-button" variant="outline-primary" onClick={() => approveRenewRequest(a)}>
                                            <img src={checkIcon} alt=""/>
                                            <span>{t('APPROVE')}</span>
                                        </Button>
                                    </td>
                                    <td className="col-md-2 text-center">
                                        <Button className="partner-affiliations-icon-button is-danger" variant="outline-secondary" onClick={() => refuseRenewRequest(a)}>
                                            <RiDeleteBinLine size="1.5em"/>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                </Col>
            </Row>
            : <div className="partner-affiliations-empty"><MdAutorenew/><h6>{renewRequestsFilter === "" ? t('NO_REQUESTS') : t('SEARCH_REQUEST_FAILED')}</h6></div>
        );
    };

    const showAffiliated = currentItems => {
        if (affiliatedPlayersLoading) {
            return (
                <div className="partner-affiliations-loading partner-affiliations-users-loading" role="status" aria-live="polite">
                    <span className="partner-affiliations-loading-icon"><Spinner animation="border"/></span>
                    <div>
                        <h5>{t('AFFILIATED_USERS_SEARCH_IN_PROGRESS')}</h5>
                        <p>{t('AFFILIATED_USERS_SEARCH_IN_PROGRESS_DESC')}</p>
                    </div>
                </div>
            );
        }

        return (
            currentItems.length > 0 ?
            <Row className="justify-content-center partner-affiliations-table-row">
                <Col xs={12} sm={12} md={12}>
                    <div className="partner-affiliations-users-table-shell">
                        <Table hover className="partner-affiliations-table partner-affiliations-users-table">
                        <thead>
                            <tr>
                                <th>{t('STATE')}</th>
                                <th>{t('MEMBERSHIP')}</th>
                                <th>{t('USER')}</th>
                                <th>{t('CONTACT_DETAILS')}</th>
                                {RANKING_SPORTS.map(sport => <th key={sport} className="text-center">{t(`SPORT_${sport.toUpperCase()}`)}</th>)}
                                <th>{t('EXPIRY')}</th>
                                <th>{t('CERTIFICATE')}</th>
                                <th className="text-center">{t('PAID')}</th>
                                <th className="text-center">{t('RENEW')}</th>
                                <th className="text-center">{t('DELETE')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {currentItems.map(a => (
                                <tr key={`${a.affiliationId}-${a.playerId}`}>
                                    <td className="col-md-1" data-label={t('STATE')}>
                                        <Form.Check
                                            id={`switch-state-${a.affiliationId}-${a.playerId}`}
                                            type="switch"
                                            className={'custom-switch'}
                                            checked={a.active}
                                            onChange={e => a.affiliation.type === affiliationType.rtv && !a.active && new Date(a.expiryTimestamp) < new Date().setHours(23, 59, 0, 0) ? confirmChangeStateDialog(e.target.checked,a) : editPlayer(a.affiliationId, a.playerId, e.target.checked, a.medicalCertificateTimestamp, a.payed)}/>
                                    </td>
                                    <td className="col-md-1" data-label={t('MEMBERSHIP')}>{a.affiliation.name}</td>
                                    <td className="col-md-1" data-label={t('USER')}>{a.player.name + ' ' + a.player.surname}</td>
                                    <td className="col-md-1" data-label={t('CONTACT_DETAILS')}>{a.player.user.email + ' ' + a.player.phoneNumber}</td>
                                    {RANKING_SPORTS.map(sport => <React.Fragment key={sport}>{renderRankingCell(a, sport)}</React.Fragment>)}
                                    <td data-label={t('EXPIRY')} className={`col-md-1 ${a.expiryTimestamp && new Date(a.expiryTimestamp) < new Date().setHours(23, 59, 0, 0) ? 'partner-affiliations-expired' : ''}`}>{a.expiryTimestamp ? new Date(a.expiryTimestamp).toLocaleDateString(): t('NOTHING')}</td>
                                    <td className="col-md-1" data-label={t('CERTIFICATE')}>
                                        <AffiliationCertificateDatePicker affiliation={a} editPlayer={editPlayer} triggerRef={getCertificateTriggerRef(a)}/>
                                    </td>
                                    <td className="col-md-1 text-center" data-label={t('PAID')}>
                                        <Form.Check
                                            id={`switch-payed-${a.affiliationId}-${a.playerId}`}
                                            disabled={affiliationPaymentCheck(a.affiliationId)}
                                            type="switch"
                                            className={'custom-switch'}
                                            checked={a.payed}
                                            onChange={e => editPlayer(a.affiliationId, a.playerId, a.active, a.medicalCertificateTimestamp, e.target.checked)}/>
                                    </td>
                                    <td className="col-md-1 text-center" data-label={t('RENEW')}>
                                        <Button
                                            className="partner-affiliations-icon-button is-renew"
                                            disabled={!isRenewalWindowOpen(a) || renewingAffiliationKey !== null}
                                            title={!isRenewalWindowOpen(a) && a.renewable ? t('RENEW_PLAYER_AFFILIATION_NOT_YET_AVAILABLE') : undefined}
                                            variant="outline-primary"
                                            onClick={() => confirmRenewPlayerAffiliation(a)}
                                        >
                                            {renewingAffiliationKey === affiliationPlayerKey(a) ? <Spinner animation="border" size="sm"/> : <MdAutorenew size="1.5em"/>}
                                        </Button>
                                    </td>
                                    <td className="col-md-1 text-center" data-label={t('DELETE')}>
                                        <Button className="partner-affiliations-icon-button is-danger" variant="outline-secondary" onClick={() => confirmDeletePlayerFromAffiliation(a.playerId, a.affiliationId)}>
                                            <RiDeleteBinLine size="1.5em"/>
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        </Table>
                    </div>
                </Col>
            </Row>
            : <div className="partner-affiliations-empty"><FaUserCheck/><h6>{affiliatedNameFilter === "" ? t('NO_AFFILIATED') : t('SEARCH_REQUEST_FAILED')}</h6></div>
        );
    };

    const endRequestsOffset = requestsOffset + 50;
    const currentRequests = playerRequests.slice(requestsOffset, endRequestsOffset);
    const requestsPageCount = Math.ceil(playerRequests.length / 50);

    const handleRequestsPageClick = (event) => {
        const newOffset = (event.selected * 50) % playerRequests.length;
        setRequestsOffset(newOffset);
    };

    const endAffiliatedOffset = affiliatedPlayersOffset + 50;
    const currentAffiliated = affiliated.slice(affiliatedPlayersOffset, endAffiliatedOffset);
    const affiliatedPageCount = Math.ceil(affiliated.length / 50);

    const handleAffiliatedPageClick = (event) => {
        const newOffset = (event.selected * 50) % affiliated.length;
        setAffiliatedPlayersOffset(newOffset);
    };

    const endRenewRequestsOffset = renewRequestsOffset + 50;
    const currentRenewRequests = renewRequests.slice(renewRequestsOffset, endRenewRequestsOffset);
    const renewRequestsPageCount = Math.ceil(renewRequests.length / 50);

    const handleRenewRequestsPageClick = (event) => {
        const newOffset = (event.selected * 50) % renewRequests.length;
        setAffiliatedPlayersOffset(newOffset);
    };


    const goToFilteredAffiliatedUserTab = affiliationId => {
        setActiveTab('affiliated_users');
        setAffiliatedAffiliationFilter(affiliationId.toString());
        setAffiliatedNameFilter("");
        setAffiliatedStateFilter("");
        setAffiliatedPlayersOffset(0);
    }

    return(
        <Fragment>
            <CommonMetaTag/>
            <main className="partner-affiliations-page">
                <section className="partner-affiliations-hero">
                    <div>
                        <span className="partner-affiliations-eyebrow">{t('AFFILIATION_MANAGEMENT')}</span>
                        <h1>{t('MEMBERSHIPS')}</h1>
                        <p>{t('AFFILIATION_MANAGEMENT_HELP')}</p>
                    </div>
                    <div className="partner-affiliations-summary" aria-label={t('MEMBERSHIPS')}>
                        <div><FaUsers/><span>{t('AFFILIATION_REQUESTS')}</span><strong>{playerRequests.length}</strong></div>
                        <div><MdAutorenew/><span>{t('AFFILIATION_RENEW_REQUESTS')}</span><strong>{renewRequests.length}</strong></div>
                        <div><FaIdBadge/><span>{t('MEMBERSHIPS')}</span><strong>{affiliations.length}</strong></div>
                        <div><FaUserCheck/><span>{t('AFFILIATED_USERS')}</span><strong>{affiliatedUniquePlayersCount}</strong></div>
                        <div><FaTrophy/><span>{t('RANKING')}</span><strong>{rankingRequests.length}</strong></div>
                    </div>
                </section>
                <Tabs activeKey={partnerAffiliationsActiveTab} className="partner-affiliations-tabs" onSelect={t => setActiveTab(t)}>
                <Tab eventKey="affiliation_requests" title={<span className="partner-affiliations-tab-content"><span className="partner-affiliations-tab-icon"><FaUserPlus/></span><span className="partner-affiliations-tab-label">{t('AFFILIATION_REQUESTS')}</span></span>} tabClassName="partner-affiliations-tab">
                    <section className="partner-affiliations-panel">
                    <div className="partner-affiliations-filter-bar">
                        <Form>
                            <Form.Group>
                                <FaSearch/>
                                <Form.Control name="requestsFilter" placeholder={t('SEARCH_USER_BY_NAME')} type="text" maxLength="50" onChange={e => setRequestsFilter(e.target.value)} onKeyPress={event => {if (event.key === 'Enter') {event.preventDefault();}}}/>
                            </Form.Group>
                        </Form>
                        <Button className="partner-affiliations-refresh-button" type="button" disabled={affiliatedPlayersLoading} onClick={refreshAffiliationRequests}>
                            <FaSyncAlt className={affiliatedPlayersLoading ? 'is-spinning' : ''}/><span>{t('UPDATE')}</span>
                        </Button>
                    </div>
                    {requests(currentRequests)}
                    <ReactPaginate
                        className="react-paginate"
                        breakLabel="..."
                        nextLabel=">"
                        onPageChange={handleRequestsPageClick}
                        pageRangeDisplayed={5}
                        pageCount={requestsPageCount}
                        previousLabel="<"
                        renderOnZeroPageCount={null}
                    />
                    </section>
                </Tab>
                <Tab eventKey="affiliation_renew_requests" title={<span className="partner-affiliations-tab-content"><span className="partner-affiliations-tab-icon"><FaSyncAlt/></span><span className="partner-affiliations-tab-label">{t('AFFILIATION_RENEW_REQUESTS')}</span></span>} tabClassName="partner-affiliations-tab">
                    <section className="partner-affiliations-panel">
                    <div className="partner-affiliations-filter-bar">
                        <Form>
                            <Form.Group>
                                <FaSearch/>
                                <Form.Control name="renewRequestsFilter" placeholder={t('SEARCH_USER_BY_NAME')} type="text" maxLength="50" onChange={e => setRenewRequestsFilter(e.target.value)} onKeyPress={event => {if (event.key === 'Enter') {event.preventDefault();}}}/>
                            </Form.Group>
                        </Form>
                        <Button className="partner-affiliations-refresh-button" type="button" onClick={refreshAffiliationRenewRequests}>
                            <FaSyncAlt/><span>{t('UPDATE')}</span>
                        </Button>
                    </div>
                    {renewRequestsTable(currentRenewRequests)}
                    <ReactPaginate
                        className="react-paginate"
                        breakLabel="..."
                        nextLabel=">"
                        onPageChange={handleRenewRequestsPageClick}
                        pageRangeDisplayed={5}
                        pageCount={renewRequestsPageCount}
                        previousLabel="<"
                        renderOnZeroPageCount={null}
                    />
                    </section>
                </Tab>
                <Tab eventKey="affiliations" title={<span className="partner-affiliations-tab-content"><span className="partner-affiliations-tab-icon"><FaAddressCard/></span><span className="partner-affiliations-tab-label">{t('MEMBERSHIPS')}</span></span>} tabClassName="partner-affiliations-tab">
                    <section className="partner-affiliations-panel">
                        <div className="partner-affiliations-panel-heading">
                            <div><h2>{t('MEMBERSHIPS')}</h2><p>{t('MEMBERSHIPS_CONFIGURATION_HELP')}</p></div>
                            <Button className="partner-affiliations-primary-button" variant="secondary" onClick={() => showAffiliationModal(availableSports)}><FaPlus/>{t('MEMBERSHIP_ADD')}</Button>
                        </div>
                        {affiliations && affiliations.length > 0 ?
                            <Row className="justify-content-center partner-affiliations-table-row">
                                <Col xs={12}>
                                    <Table hover responsive className="partner-affiliations-table partner-affiliations-memberships-table">
                                        <thead>
                                            <tr>
                                                <th>{t('MEMBERSHIP')}</th>
                                                <th className="text-center">{t('USERS')}</th>
                                                <th className="text-center">{t('CHANGE')}</th>
                                                <th className="text-center">{t('ONOFF')}</th>
                                                <th className="text-center">{t('DELETE')}</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {affiliations.map(a => (
                                                <tr key={a.affiliationId}>
                                                <td className="partner-affiliations-membership-name">{a.name}</td>
                                                <td className="partner-affiliations-membership-action text-center">{affiliationPlayerCount(a) > 0 ? <button className="partner-affiliations-icon-button is-users" type="button" onClick={() => goToFilteredAffiliatedUserTab(a.affiliationId)}><FaUser size="1.2em"/><span>{affiliationPlayerCount(a)}</span></button> : <span className="partner-affiliations-muted">—</span>}</td>
                                                <td className="partner-affiliations-membership-action text-center">{a.type === affiliationType.partner ? <button className="partner-affiliations-icon-button is-edit" type="button" onClick={() => showEditAffiliationModal(a, availableSports)}><FaPencilAlt size="1.1em"/></button> : <span className="partner-affiliations-muted">—</span>}</td>
                                                <td className="partner-affiliations-membership-action text-center">
                                                    <Form.Check
                                                    id={`switch-${a.affiliationId}`}
                                                    className={'custom-switch'}
                                                    type="switch"
                                                    checked={a.active}
                                                    onChange={e => activateDeactivateAffiliationDialog(e.target.checked, a)}/>
                                                </td>
                                                <td className="partner-affiliations-membership-action text-center">{a.type === affiliationType.partner ? <button className="partner-affiliations-icon-button is-danger" type="button" onClick={() => deleteAffiliationDialog(a)}><FaTrashAlt size="1.1em"/></button> : <span className="partner-affiliations-muted">—</span>}</td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </Table>
                                </Col>
                            </Row>
                        :
                        <Fragment>
                            <div className="partner-affiliations-empty">
                                <FaIdBadge/>
                                <h5>
                                    {t('NO_AFFILIATION')}
                                    <button type="button" className="btn btn-link p-0" onClick={() => showAffiliationModal(availableSports)}>{t('NO_AFFILIATION_TWO')}</button>
                                    {t('NO_AFFILIATION_THREE')}
                                </h5>
                            </div>
                        </Fragment>}
                        <Modal show={isAffiliationModalVisible} onHide={hideAffiliationModal} dialogClassName="partner-affiliations-modal partner-affiliations-management-modal" centered>
                            <Modal.Header closeButton>
                                <Modal.Title>{selectedAffiliation && selectedAffiliation.affiliationId ? t('MEMBERSHIP_ADMINISTRATION') : t('MEMBERSHIP_ADD')}</Modal.Title>
                            </Modal.Header>
                            <Modal.Body>
                                <Formik
                                    initialValues={selectedAffiliation}
                                    onSubmit={newOrEditAffiliation}
                                >
                                    {({
                                    handleSubmit,
                                    handleChange,
                                    setFieldValue,
                                    values
                                    }) => {
                                    return (
                                        <Form onSubmit={handleSubmit}>
                                            <Form.Group>
                                                <Form.Label>{t('NAME')}*</Form.Label>
                                                <Form.Control required maxLength="70" type="text" name="name" onChange={handleChange} value={values.name || ''}/>
                                            </Form.Group>
                                            <Form.Group>
                                                <Form.Label>{t('DESCRIPTION')}</Form.Label>
                                                <Form.Control maxLength="255" type="text" name="description" onChange={handleChange} value={values.description || ''}/>
                                            </Form.Group>
                                            <Form.Group>
                                                <Form.Label>{t('MEMBERSHIP_VISIBILITY_CALENDAR')}*</Form.Label>
                                                <Form.Control required name="visibilityCalendar" type="number" min="0" max="90" step="1" value={values.visibilityCalendar} onChange={handleChange}/>
                                            </Form.Group>
                                            <Form.Group className="partner-affiliations-pricing-mode">
                                                <Form.Label>{t('AFFILIATION_PRICING_MODE')}*</Form.Label>
                                                <Form.Check required name="pricingMode" type="radio" label={t('AFFILIATION_WITH_DISCOUNT')} checked={(values.pricingMode || AFFILIATION_PRICING_MODE.discount) === AFFILIATION_PRICING_MODE.discount} onChange={() => {
                                                    setFieldValue('pricingMode', AFFILIATION_PRICING_MODE.discount);
                                                    setFieldValue('discount', Number(values.discount || 0));
                                                }}/>
                                                <Form.Check required name="pricingMode" type="radio" disabled={availableSports.length === 0} label={t('AFFILIATION_WITH_DEDICATED_FIELD_COST')} checked={values.pricingMode === AFFILIATION_PRICING_MODE.dedicatedFieldCost} onChange={() => {
                                                    setFieldValue('pricingMode', AFFILIATION_PRICING_MODE.dedicatedFieldCost);
                                                    setFieldValue('discount', 0);
                                                    setFieldValue('sportPricing', buildSportPricing(availableSports, values.sportPricing || []));
                                                }}/>
                                                {availableSports.length === 0 ? <Form.Text>{t('DEDICATED_FIELD_COST_REQUIRES_BOOKABLE_SPORT')}</Form.Text> : null}
                                            </Form.Group>
                                            {(values.pricingMode || AFFILIATION_PRICING_MODE.discount) === AFFILIATION_PRICING_MODE.discount ? (
                                                <Form.Group>
                                                    <Form.Label>{t('DISCOUNT')}*</Form.Label>
                                                    <Form.Control required type="number" min={0} max={100} name="discount" onChange={handleChange} value={values.discount}/>
                                                </Form.Group>
                                            ) : (
                                                <div className="partner-affiliations-sport-pricing">
                                                    <p className="partner-affiliations-pricing-help">{t('DEDICATED_FIELD_COST_CONFIGURATION_HELP')}</p>
                                                    {(values.sportPricing || []).map((pricing, sportIndex) => (
                                                        <section key={pricing.sport} className="partner-affiliations-sport-pricing-section">
                                                            <h4>{sportLabel(pricing.sport)}</h4>
                                                            <Form.Group>
                                                                <Form.Label>{t('NORMAL_BOOKING_SPORT_DISCOUNT')}*</Form.Label>
                                                                <Form.Control required type="number" min={0} max={100} name={`sportPricing.${sportIndex}.normalDiscount`} onChange={handleChange} value={pricing.normalDiscount}/>
                                                            </Form.Group>
                                                            <AffiliationParticipantRates handleChange={handleChange} pricing={pricing} sportIndex={sportIndex} t={t}/>
                                                        </section>
                                                    ))}
                                                </div>
                                            )}
                                            <Form.Group>
                                                <Form.Label>{t('TYPE')}*</Form.Label>
                                                <Form.Check required name="renewalType" type="radio" label={t('NO_RENEWAL')} checked={values.renewalType === renewalType.lifeTime} onChange={() => setFieldValue('renewalType', renewalType.lifeTime)}/>
                                                <Form.Check required name="renewalType" type="radio" label={t('WITH_RENEWAL')} checked={values.renewalType !== renewalType.lifeTime} onChange={() => setFieldValue('renewalType', renewalType.perPlayer)}/>
                                            </Form.Group>
                                            {values.renewalType !== renewalType.lifeTime ?
                                                <div>
                                                    <Form.Group>
                                                        <Form.Label>{t('SUBSCRIPTION_DURATION')}*</Form.Label>
                                                        <Form.Control required as="select" name="durationType" onChange={handleChange} value={values.durationType}>
                                                            <option value="">{t('SELECT_DURATION')}</option>
                                                            <option value={durationType.days}>{t('DAYS')}</option>
                                                            <option value={durationType.months}>{t('MONTHS')}</option>
                                                            <option value={durationType.years}>{t('YEAR')}</option>
                                                        </Form.Control>
                                                    </Form.Group>
                                                    <Form.Group>
                                                        <Form.Control required type="number" min={getMinimumDuration(values.durationType)} max={getMaximumDuration(values.durationType)} name="duration" placeholder={t('QUANTITY')} onChange={handleChange} value={values.duration || null}/>
                                                    </Form.Group>
                                                    <Form.Group>
                                                        <Form.Label>{t('SUBSCRIPTION_FIRST_PERIOD_START')}</Form.Label>
                                                        <Form.Check required name="renewalTypeTwo" type="radio" label={t('DAY_OF_APPROVAL')} checked={values.renewalType === renewalType.perPlayer} onChange={() => setFieldValue('renewalType', renewalType.perPlayer)}/>
                                                        <Form.Check required name="renewalTypeTwo" type="radio" label={t('IN_THIS_DATE')} checked={values.renewalType === renewalType.fixed} onChange={() => setFieldValue('renewalType', renewalType.fixed)}/>
                                                    {
                                                        values.renewalType === renewalType.fixed ?
                                                        <DatePicker
                                                            locale="it"
                                                            className="form-control"
                                                            dateFormat="dd/MM/yyyy"
                                                            selected={(values.fixedDatetime && new Date(values.fixedDatetime)) || null}
                                                            minDate={values.durationType === durationType.days ? new Date().setDate(new Date().getDate() - values.duration) :
                                                                values.durationType === durationType.months ? new Date().setMonth(new Date().getMonth() - values.duration) :
                                                                values.durationType === durationType.years ? new Date().setFullYear(new Date().getFullYear() - values.duration) :
                                                                 new Date()}
                                                                 onChange={val => {
                                                                    const updatedVal = new Date(val);
                                                                    updatedVal.setHours(12);
                                                                    setFieldValue('fixedDatetime', updatedVal);
                                                                }}
                                                        />
                                                        : null
                                                    }
                                                    </Form.Group>
                                                </div>
                                            : null}
                                            <Form.Group>
                                                <Form.Label>{t('FOR_A_FEE')}*</Form.Label>
                                                <Form.Check required name="forAFee" type="radio" label={t('YES')} checked={values.onlinePayment !== null} onChange={() => setFieldValue('onlinePayment', false)}/>
                                                <Form.Check required name="forAFee" type="radio" label={t('NO')} checked={values.onlinePayment === null} onChange={() => setFieldValue('onlinePayment', null)}/>
                                            </Form.Group>
                                            {values.onlinePayment !== null && profile && profile.affiliationOnlinePayment ?
                                                <Form.Group>
                                                    <Form.Label>{t('ONLINE_PAYMENT')}*</Form.Label>
                                                    <Form.Check required name="onlinePayment" type="radio" label={t('YES')} checked={values.onlinePayment} onChange={() => setFieldValue('onlinePayment', true)}/>
                                                    <Form.Check required name="onlinePayment" type="radio" label={t('NO')} checked={!values.onlinePayment} onChange={() => setFieldValue('onlinePayment', false)}/>
                                                </Form.Group>
                                            : null}
                                            {values.onlinePayment ?
                                                <Form.Group>
                                                    <Form.Label>{t('AFFILIATION_PRICE')}*</Form.Label>
                                                    <Form.Control required type="number" min={1} name="price" onChange={handleChange} value={values.price || null}/>
                                                </Form.Group>
                                            : null}
                                            <Button type="submit" className="partner-affiliations-primary-button" variant="secondary">
                                                <FaCheck/>{t('SAVE')}
                                            </Button>
                                        </Form>
                                    );
                                    }}
                                </Formik>
                            </Modal.Body>
                            <Modal.Footer>
                                <Button className="partner-affiliations-secondary-button" variant="primary" onClick={hideAffiliationModal}>
                                    {t('CLOSE_BUTTON')}
                                </Button>
                            </Modal.Footer>
                        </Modal>
                    </section>
                </Tab>
                <Tab eventKey="ranking_verifications" title={<span className="partner-affiliations-tab-content"><span className="partner-affiliations-tab-icon"><FaTrophy/></span><span className="partner-affiliations-tab-label">{t('RANKING')}</span></span>} tabClassName="partner-affiliations-tab">
                    <section className="partner-affiliations-panel">
                        <div className="partner-affiliations-panel-heading">
                            <div><h2>{t('RANKING_CONFIRMATIONS')}</h2><p>{t('RANKING_CONFIRMATIONS_HELP')}</p></div>
                            <Button className="partner-affiliations-refresh-button" type="button" disabled={rankingRequestsLoading} onClick={refreshRankingRequests}>
                                <FaSyncAlt className={rankingRequestsLoading ? 'is-spinning' : ''}/><span>{t('UPDATE')}</span>
                            </Button>
                        </div>
                        {rankingRequestsMessage ? <div className="partner-ranking-message">{rankingRequestsMessage}</div> : null}
                        {rankingRequestsLoading ? (
                            <div className="partner-affiliations-loading"><Spinner animation="border"/><span>{t('RANKING_REQUESTS_LOADING')}</span></div>
                        ) : rankingRequests.length > 0 ? (
                            <Table hover responsive className="partner-affiliations-table partner-ranking-verification-table">
                                <thead>
                                    <tr>
                                        <th>{t('PLAYER')}</th>
                                        <th>{t('SPORT')}</th>
                                        <th>{t('RANKING')}</th>
                                        <th>{t('FEDERATION_LICENSE')}</th>
                                        <th>{t('CURRENT_CLASSIFICATION')}</th>
                                        <th>{t('BEST_CLASSIFICATION')}</th>
                                        <th>{t('NOTE')}</th>
                                        <th className="text-center">{t('ACTIONS')}</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {rankingRequests.map(request => {
                                        const edit = rankingRequestEdits[request.id] || {};
                                        const ranking = request.ranking || {};
                                        const player = request.player || {};
                                        return (
                                            <tr key={request.id}>
                                                <td><strong>{`${player.name || ''} ${player.surname || ''}`.trim() || player.nickname || `Player #${request.uid}`}</strong></td>
                                                <td>{request.sport}</td>
                                                <td><strong>{ranking.displayLevel || ranking.level ? Number(ranking.displayLevel || ranking.level).toFixed(1) : '-'}</strong><small>{t('RELIABILITY_PERCENT', {value: ranking.ratingReliability || ranking.reliability || 0})}</small></td>
                                                <td>
                                                    <Form.Control
                                                        type="text"
                                                        value={edit.licenseNumber || ''}
                                                        placeholder={t('FEDERATION_LICENSE')}
                                                        onChange={event => updateRankingRequestEdit(request.id, {licenseNumber: event.target.value})}
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="text"
                                                        value={edit.classification || ''}
                                                        placeholder={t('CLASSIFICATION_EXAMPLE')}
                                                        onChange={event => updateRankingRequestEdit(request.id, {classification: event.target.value})}
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="text"
                                                        value={edit.bestClassification || ''}
                                                        placeholder={t('OPTIONAL')}
                                                        onChange={event => updateRankingRequestEdit(request.id, {bestClassification: event.target.value})}
                                                    />
                                                </td>
                                                <td>
                                                    <Form.Control
                                                        type="text"
                                                        value={edit.note || ''}
                                                        placeholder={t('INTERNAL_NOTE')}
                                                        onChange={event => updateRankingRequestEdit(request.id, {note: event.target.value})}
                                                    />
                                                </td>
                                                <td className="partner-ranking-actions">
                                                    <Button className="partner-affiliations-primary-button" disabled={rankingRequestsLoading || !edit.classification} onClick={() => reviewRankingRequest(request, 'confirm')}><FaCheck/>{t('CONFIRM')}</Button>
                                                    <Button className="partner-affiliations-secondary-button" disabled={rankingRequestsLoading} onClick={() => reviewRankingRequest(request, 'reject')}>{t('REJECT')}</Button>
                                                </td>
                                            </tr>
                                        );
                                    })}
                                </tbody>
                            </Table>
                        ) : (
                            <div className="partner-affiliations-empty"><FaTrophy/><h6>{t('NO_PENDING_RANKING_REQUESTS')}</h6></div>
                        )}
                    </section>
                </Tab>
                <Tab eventKey="affiliated_users" title={<span className="partner-affiliations-tab-content"><span className="partner-affiliations-tab-icon"><FaUserCheck/></span><span className="partner-affiliations-tab-label">{t('AFFILIATED_USERS')}</span></span>} tabClassName="partner-affiliations-tab">
                    <section className="partner-affiliations-panel">
                    <div className="partner-affiliations-filter-bar is-multiple">
                        <Form>
                        <Form.Group>
                            <Row>
                                <div className="partner-affiliations-filter-field partner-affiliations-filter-field-search">
                                    <Form.Label>{t('USER')}</Form.Label>
                                    <div className="partner-affiliations-search"><FaSearch/><Form.Control disabled={affiliatedPlayersLoading} name="affiliatedNameFilter" value={affiliatedNameFilter} placeholder={t('SEARCH_USER_BY_NAME')} type="text" maxLength="50" onChange={e => {setAffiliatedNameFilter(e.target.value); setAffiliatedPlayersOffset(0);}} onKeyPress={event => {if (event.key === 'Enter') {event.preventDefault();}}}/></div>
                                </div>
                                <div className="partner-affiliations-filter-field">
                                    <Form.Label>{t('MEMBERSHIP')}</Form.Label>
                                    <Form.Control disabled={affiliatedPlayersLoading} value={affiliatedAffiliationFilter} name="affiliationFilter" as="select" onChange={e => {setAffiliatedAffiliationFilter(e.target.value); setAffiliatedPlayersOffset(0);}}>
                                        <option value={""}>{t('ALL_AFFILIATIONS')}</option>
                                        {affiliatedAffiationOptions.map(o => (<option key={o.value} value={o.value}>{o.label}</option>))}
                                    </Form.Control>
                                </div>
                                <div className="partner-affiliations-filter-field">
                                    <Form.Label>{t('STATE')}</Form.Label>
                                    <Form.Control disabled={affiliatedPlayersLoading} value={affiliatedStateFilter} name="stateFilter" as="select" onChange={e => {setAffiliatedStateFilter(e.target.value); setAffiliatedPlayersOffset(0);}}>
                                        <option value={""}>{t('ALL_STATES')}</option>
                                        <option value={"active"}>{t('ACTIVE')}</option>
                                        <option value={"nonactive"}>{t('NONACTIVE')}</option>
                                        <option value={"payed"}>{t('PAID')}</option>
                                        <option value={"toBePayed"}>{t('TO_PAY')}</option>
                                        <option value={"expired"}>{t('EXPIRED')}</option>
                                        <option value={"expiredCertificate"}>{t('EXPIRED_CERTIFICATE')}</option>
                                        <option value={"noCertificate"}>{t('NO_CERTIFICATE')}</option>
                                    </Form.Control>
                                </div>
                            </Row>
                        </Form.Group>
                        </Form>
                    </div>
                    {showAffiliated(currentAffiliated)}
                    <ReactPaginate
                        className="react-paginate"
                        breakLabel="..."
                        nextLabel=">"
                        onPageChange={handleAffiliatedPageClick}
                        pageRangeDisplayed={5}
                        pageCount={affiliatedPageCount}
                        previousLabel="<"
                        renderOnZeroPageCount={null}
                    />
                    </section>
                </Tab>
            </Tabs>
            </main>
            <Modal show={Boolean(rankingAssessment)} onHide={() => !rankingAssessmentLoading && setRankingAssessment(null)} centered dialogClassName="partner-affiliations-modal">
                <Modal.Header closeButton>
                    <Modal.Title>{t('PARTNER_SET_REPLAYER_LEVEL')}</Modal.Title>
                </Modal.Header>
                <Modal.Body>
                    {rankingAssessment ? (
                        <Fragment>
                            <p className="partner-ranking-assessment-intro">
                                {t('PARTNER_RANKING_ASSESSMENT_HELP', {
                                    player: rankingAssessment.playerName,
                                    sport: t(`SPORT_${rankingAssessment.sport.toUpperCase()}`)
                                })}
                            </p>
                            {rankingAssessmentMessage ? <div className="partner-ranking-message">{rankingAssessmentMessage}</div> : null}
                            <Form.Group>
                                <Form.Label>{t('REPLAYER_LEVEL')}</Form.Label>
                                <Form.Control
                                    type="number"
                                    min="1"
                                    max="10"
                                    step="0.1"
                                    value={rankingAssessment.level}
                                    onChange={event => setRankingAssessment({...rankingAssessment, level: event.target.value})}
                                />
                                <Form.Text>{t('PARTNER_RANKING_LEVEL_RANGE_HELP')}</Form.Text>
                            </Form.Group>
                            <Form.Group>
                                <Form.Label>{t('NOTE')}</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    maxLength={255}
                                    value={rankingAssessment.note}
                                    onChange={event => setRankingAssessment({...rankingAssessment, note: event.target.value})}
                                    placeholder={t('PARTNER_RANKING_NOTE_PLACEHOLDER')}
                                />
                            </Form.Group>
                        </Fragment>
                    ) : null}
                </Modal.Body>
                <Modal.Footer>
                    <Button className="partner-affiliations-secondary-button" disabled={rankingAssessmentLoading} onClick={() => setRankingAssessment(null)}>{t('CANCEL')}</Button>
                    <Button
                        className="partner-affiliations-primary-button"
                        disabled={rankingAssessmentLoading || !rankingAssessment || Number(rankingAssessment.level) < 1 || Number(rankingAssessment.level) > 10}
                        onClick={saveRankingAssessment}
                    >
                        {rankingAssessmentLoading ? <Spinner animation="border" size="sm"/> : <FaCheck/>}{t('SAVE')}
                    </Button>
                </Modal.Footer>
            </Modal>
            <Dialog
              ref={el => {
                dialog = el;
              }}/>
        </Fragment>
    );
};

const mapStateToProps = ({login: {profile, token}, partner: {isAffiliationModalVisible, affiliations, affiliationsStatus, selectedAffiliation, affiliatedPlayers, affiliatedPlayersLoading, partnerAffiliationsActiveTab, affiliationRenewRequests, ownedFields, ownedFieldsStatus}}) => ({
    profile,
    token,
    isAffiliationModalVisible,
    affiliations,
    affiliationsStatus,
    selectedAffiliation,
    affiliatedPlayers,
    affiliatedPlayersLoading,
    partnerAffiliationsActiveTab,
    affiliationRenewRequests,
    ownedFields,
    ownedFieldsStatus
  });

const mapDispatchToProps = dispatch => ({
    showAffiliationModal: (availableSports = []) => {
        dispatch(selectAffiliation({name: '', description: null, discount: 0, pricingMode: AFFILIATION_PRICING_MODE.discount, sportPricing: buildSportPricing(availableSports), renewalType: renewalType.lifeTime, onlinePayment: null, price: null, visibilityCalendar: 0}));
        dispatch(showAffiliationModal());
    },
    hideAffiliationModal: () => {
        dispatch(hideAffiliationModal());
    },
    newOrEditAffiliation: values => {
        if ("durationType" in values && values.durationType !== null) {
            values.durationType = parseInt(values.durationType, 10);
        }
        if(values.renewalType === renewalType.lifeTime) {
            delete values.duration;
            delete values.durationType;
        }
        if (values.renewalType !== renewalType.fixed) {
            delete values.fixedDatetime;
        }
        if(!values.onlinePayment) {
            delete values.price;
        }
        if(values.affiliationId) {
            dispatch(editAffiliation(values));
        }
        else {
            dispatch(createAffiliation(values));
        }
    },
    activateDeactivateAffiliation: (active, a) => {
        dispatch(activateDeactivateAffiliation(a.affiliationId,active));
    },
    deleteAffiliation: affiliationId => {
        dispatch(deleteAffiliation(affiliationId));
    },

    showEditAffiliationModal: (affiliation, availableSports = []) => {
        dispatch(selectAffiliation({
            ...affiliation,
            pricingMode: affiliation.pricingMode || AFFILIATION_PRICING_MODE.discount,
            sportPricing: buildSportPricing(availableSports, affiliation.sportPricing || [])
        }));
        dispatch(showAffiliationModal());
    },
    setActiveTab: tab => {
        dispatch(setPartnerAffiliationsActiveTab(tab));
        if (shouldRefreshAffiliatedPlayers(tab)) {
            dispatch(getAffiliatedPlayers());
        }
    },
    deletePlayerFromAffiliation: (playerId,affiliationId) => {
        dispatch(deletePlayerFromAffiliation(playerId,affiliationId));
    },
    editPlayer: (affiliationId, playerId, active, medicalCertificateTimestamp, payed) => {
        return dispatch(editAffiliationPlayer(affiliationId, playerId, active, medicalCertificateTimestamp, payed));
    },
    renewPlayerAffiliation: (playerId, affiliationId) => {
        return dispatch(renewPlayerAffiliation(playerId, affiliationId))
    },
    approveRenewRequest: request => {
        dispatch(acceptOrRefuseAffiliationRenewRequest(request, true));
    },
    refuseRenewRequest: request => {
        dispatch(acceptOrRefuseAffiliationRenewRequest(request, false));
    },
    refreshAffiliationRequests: () => {
        dispatch(getAffiliatedPlayers());
    },
    refreshAffiliationRenewRequests: () => {
        dispatch(getAffiliationRenewRequests());
    }
});

  export default withRouter(connect(
    mapStateToProps,
    mapDispatchToProps
  )(PartnerAffiliations));
